'use client'

import * as React from 'react'
import { useWindowDomEvent } from 'hooks/use-dom-event'
import { tw } from 'utils/classnames'
import { motion, useAnimationControls } from 'framer-motion'
import { Scroller } from 'primitives/scroller'

import { Filter } from './filter'
import { TagsSection } from './tag-section'
import { SortOption } from './sort-option'
import { FilterMobile, FilterMobileStudio } from './filter-mobile'
import { SEARCHABLE_FILTERS } from './constants'

const animationVariants = {
  shift: {
    y: 64,
    transition: { duration: 0.3, ease: 'easeInOut' as const },
  },
  unshift: {
    y: 0,
    transition: { duration: 0.3, ease: 'easeInOut' as const },
  },
}

interface FiltersSectionProps {
  isFavorites?: boolean
  favoritesText: string
  sortText: string
  filtersText: string
  allTemplatesText: string
  subcategoriesText: string
}
export function FiltersSection({
  isFavorites = false,
  favoritesText,
  allTemplatesText,
  subcategoriesText,
  filtersText,
  sortText,
}: FiltersSectionProps) {
  const { controls, isFilterSectionSticky } = useFiltersSection()

  return (
    <>
      <TagsSection />
      <div className="font-inter mt-2 flex flex-wrap items-center justify-between gap-1 sm:gap-2">
        <div className="mb-2 hidden flex-wrap gap-1 lg:flex">
          {/* <ColorFilter title="Colors" /> */}
          <Filters />
        </div>
        <div id="mobile-filter-section" />
        <motion.div
          layout
          initial="unshift"
          animate={controls}
          variants={animationVariants}
          className={tw(
            'flex w-full items-center justify-between lg:hidden lg:w-auto',
            isFilterSectionSticky
              ? 'bg-service-white-white fixed left-0 top-0 z-30 border-b border-gray-800 px-4 py-2 shadow-[0px_10px_10px_0px_rgba(0,_0,_0,_0.04)]'
              : 'relative',
          )}
        >
          <FilterMobile
            isFavorites={isFavorites}
            favoritesText={favoritesText}
            allTemplatesText={allTemplatesText}
            subcategoriesText={subcategoriesText}
            filtersText={filtersText}
          />
          <div className="flex items-center">
            <span className="text-14 font-medium text-gray-300">{sortText}</span>
            <SortOption />
          </div>
        </motion.div>
        <div className="hidden items-center justify-between lg:flex">
          <div className="flex items-center">
            <span className="text-14 font-medium text-gray-300">{sortText}</span>
            <SortOption />
          </div>
        </div>
      </div>
    </>
  )
}

type StyleCSS = React.CSSProperties & Record<string, string>
export function FiltersSectionStudio({ sortText }: FiltersSectionProps) {
  const { controls } = useFiltersSection()
  const filtersCount = Object.values(SEARCHABLE_FILTERS)?.length || 0

  return (
    <>
      <TagsSection />
      <div className="font-inter mt-2 flex lg:hidden">
        <div className="flex w-fit items-center gap-1 pr-1 lg:hidden">
          <motion.div
            animate={controls}
            className="relative mb-2 flex w-fit items-start justify-between lg:mb-[unset] lg:hidden lg:w-auto"
          >
            <FilterMobileStudio />
          </motion.div>
        </div>
        <div
          style={
            {
              '--gap': '4px',
              '--filters-gap': `${filtersCount * 4}px`,
              '--all-filters': '51px',
              '--gutter': '16px',
            } as StyleCSS
          }
          className="w-full max-w-[calc(100%-var(--filters-gap)-var(--all-filters)-(var(--gutter))-(var(--gutter)/2))] sm:max-w-[calc(100%-var(--filters-gap)-var(--all-filters)-(var(--gutter))-(var(--gutter)))]"
        >
          <FiltersMobileWithScroll filterContentClassName="max-w-[90dvw]" />
        </div>
      </div>
      <div className="font-inter mt-2 flex flex-wrap items-center justify-between gap-1 sm:gap-2">
        <div className="mb-2 hidden flex-wrap gap-1 lg:flex">
          {/* <ColorFilter title="Colors" /> */}
          <Filters />
        </div>
        <div className="font-inter mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-14 font-[500] text-gray-300">{sortText}</span>
            <SortOption />
          </div>
        </div>
      </div>
    </>
  )
}

interface FiltersMobileWithScrollProps {
  filterContentClassName?: string
}
function FiltersMobileWithScroll({ filterContentClassName }: FiltersMobileWithScrollProps) {
  return (
    <Scroller.Root>
      <Scroller.ScrollArea gutter={0} gap={4}>
        {/* <ColorFilter title="Colors" /> */}
        <Filters filterContentClassName={filterContentClassName} />
      </Scroller.ScrollArea>
    </Scroller.Root>
  )
}

interface FiltersProps {
  filterContentClassName?: string
}
function Filters({ filterContentClassName }: FiltersProps) {
  return Object.values(SEARCHABLE_FILTERS).map(filter => (
    <Filter key={filter.label} filter={filter} filterContentClassName={filterContentClassName} />
  ))
}

function useFiltersSection() {
  const lastScrollY = React.useRef(0)
  const [isFilterSectionSticky, setIsFilterSectionSticky] = React.useState(false)
  const [isFilterSectionShifted, setIsFilterSectionShifted] = React.useState(false)
  const controls = useAnimationControls()

  const handleScroll = React.useCallback(() => {
    // oxlint-disable-next-line no-negated-condition
    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0
    if (currentScrollY < lastScrollY.current) {
      setIsFilterSectionShifted(true)
    } else if (currentScrollY > lastScrollY.current && currentScrollY > 64) {
      setIsFilterSectionShifted(false)
    }
    lastScrollY.current = currentScrollY

    // oxlint-disable-next-line unicorn/prefer-query-selector
    const elementToWatch = document.getElementById('mobile-filter-section')

    if (window.scrollY > 0) {
      // oxlint-disable-next-line unicorn/no-lonely-if
      if (elementToWatch) {
        const elementPosition = elementToWatch.getBoundingClientRect()
        if (elementPosition.top <= 0) {
          setIsFilterSectionSticky(true)
        } else if (elementPosition.top >= 111) {
          setIsFilterSectionSticky(false)
        }
      }
    }
  }, [])

  React.useEffect(() => {
    if (isFilterSectionSticky && isFilterSectionShifted) {
      controls.start('shift')
    }
    if (!isFilterSectionShifted || !isFilterSectionSticky) {
      controls.start('unshift')
    }
  }, [isFilterSectionShifted, isFilterSectionSticky, controls])

  useWindowDomEvent('scroll', handleScroll, { passive: true })

  return { controls, isFilterSectionSticky }
}
